import React, { useState } from 'react'
import { numberToCurrency } from '@/shared/helpers/String'
import AddToCartButton from '@/public/product/AddToCartButton'
import ApplicationButton from '@/public/product/ApplicationButton'
import PolicyLinks from '@/public/product/PolicyLinks'
import PropTypes from 'prop-types'
import UseCart from '@/public/hooks/use-cart'
import PriceBlockMembershipOptions from './PriceBlockMembershipOptions'

export default function PriceBlockWithoutQuantity({
  product,
  returnPolicy,
  shippingPolicy,
  planTerms,
  customerIsMember,
  accessMembershipPrice,
  annualAccessMembershipPrice,
  serviceFee = null,
  labCompanyName = null,
  debug = false,
  defaultMembershipOption = null,
  membershipPlans = [],
  membershipPlansEnabled = false,
  embedded = false,
}) {
  const { cart, isLoading: cartLoading, isError: cartError } = UseCart()
  const cartContainsMembership = cart?.contents?.some((item) => item.data.category === 'membership')
  // Behind the flag the shopper picks a plan, so the selection IS a SKU and the
  // cheapest plan is pre-selected -- it is the one the cart would auto-add
  // anyway, so the page and the cart agree before the shopper touches anything.
  const hasPlans = membershipPlans.length > 0
  const [selectedOption, setSelectedOption] = useState(
    hasPlans ? membershipPlans[0].sku : defaultMembershipOption,
  )
  // A non-member buying a lab or test needs a membership; the picker is how they
  // choose one, and it is hidden once the cart already holds one.
  const membershipRequired = (product.category === 'lab' || product.category === 'test') && !customerIsMember
  // Behind the flag only a plan ON SALE counts as a membership the shopper has
  // already chosen -- the same rule the endpoints apply. A pre-rollout ACCESS
  // line would otherwise hide the picker while the server rejects it as a
  // choice, so adding a lab evicts it and auto-adds the cheapest plan and the
  // shopper never gets to pick among the plans this rollout exists to offer.
  const cartHoldsPlanOnSale = membershipPlans.some(
    (plan) => (cart?.contents || []).some((line) => line.data?.sku === plan.sku),
  )
  const cartMembershipCounts = membershipPlansEnabled ? cartHoldsPlanOnSale : cartContainsMembership
  const membershipPickerShown = membershipRequired && !cartMembershipCounts
  // The outgoing SKU is gated on the picker rather than on the plan list alone:
  // every price block receives the list, so a supplement page would otherwise
  // post the cheapest plan's SKU and have a membership added alongside a
  // product that never asked for one, with no control on screen the shopper
  // could have used to decline it.
  const membershipSku = hasPlans && membershipPickerShown ? selectedOption : ''
  // Flag on with nothing on sale: the picker says the order cannot be placed
  // yet, and this is what makes that true. Without it the shopper could add the
  // lab anyway and reach checkout with no membership behind it -- the one thing
  // the membership gate on labs exists to prevent.
  //
  // Keyed on membershipRequired, NOT on the picker being visible. A cart already
  // holding a membership hides the picker, but if that membership is stale the
  // refresh evicts it and finds no plan to auto-add -- so the shopper would be
  // left with exactly the lab-without-a-membership this guards against, and no
  // control on screen would have warned them.
  const membershipUnavailable = membershipRequired && membershipPlansEnabled && !hasPlans
  // Left exactly as it was. The legacy param is posted from every price block
  // today, including ones that render no picker, and narrowing it here would
  // change flag-off behaviour -- see the PR description.
  const membershipOption = hasPlans ? '' : selectedOption

  // The service fee is charged once per distinct lab company per order, so the
  // raw price is the price and the fee is its own line. Cart-aware where the cart
  // is readable; when it is not (still loading or errored), degrade to the
  // truthful rule-stating copy rather than guessing.
  const fee = Number(serviceFee)
  const showServiceFee = fee > 0 && Number(product.price) > 0
  const cartUnknown = cartLoading || cartError
  const labCompanyInCart = !cartUnknown && (cart?.contents || []).some(
    (item) => item.data?.category === 'lab' && item.data?.service_category_id === product.service_category_id,
  )

  const serviceFeeNote = () => {
    if (cartUnknown) {
      return `+ ${numberToCurrency(serviceFee)} one-time ${labCompanyName} service fee — charged once per order`
    }
    if (labCompanyInCart) {
      return `✓ Your order already includes the ${labCompanyName} service fee — no additional fee for this test`
    }
    return `+ ${numberToCurrency(serviceFee)} one-time ${labCompanyName} service fee — charged once per order, covers all your ${labCompanyName} tests`
  }

  const handleOptionChange = (event) => {
    setSelectedOption(event.target.value)
  }

  // The fee note + actions are shared between the standalone card (default) and
  // the embedded mode, where the merged lab-company card in
  // products/_lab_company_variant owns the outer card, the price header, and the
  // dashed divider above the selector. In embedded mode we render only this body
  // (no card wrapper, no price header, no leading <hr>) so it slots in below the
  // company pills inside that card.
  const feeAndActions = (
    <>
      {showServiceFee && (
        <p className="text-muted small mb-0 mt-1 lab-company-fee-note">{serviceFeeNote()}</p>
      )}

      <div className="actions">
        {!embedded && <hr className="mb-3" />}

        {product.is_applicable
          && (
            <ApplicationButton
              product={product}
              button_text={product.button_text}
              product_url={product.application_url}
              target="_blank"
              className="btn btn-primary btn-block"
            />
          )}

        {membershipPickerShown && (
          <PriceBlockMembershipOptions
            selectedOption={selectedOption}
            handleOptionChange={handleOptionChange}
            membershipPlans={membershipPlans}
            membershipPlansEnabled={membershipPlansEnabled}
            accessMembershipPrice={accessMembershipPrice}
            annualAccessMembershipPrice={annualAccessMembershipPrice}
            product={product}
          />
        )}

        {product.is_purchasable
          && (
            <AddToCartButton
              product={product}
              className="btn btn-primary btn-block"
              membershipOption={membershipOption}
              membershipSku={membershipSku}
              disabled={membershipUnavailable}
            />
          )}

        <PolicyLinks
          product={product}
          shippingPolicy={shippingPolicy}
          returnPolicy={returnPolicy}
          planTerms={planTerms}
        />
      </div>
    </>
  )

  if (embedded) {
    return feeAndActions
  }

  return (
    <div className="card">
      <div className="card-content pt-4">
        <div className="subtitle bold fs-4 d-flex flex-column flex-sm-row">
          <div>
            <span>{numberToCurrency(product.price)}</span>
            {(product.category === 'lab' || product.category === 'test') && !customerIsMember && (
              <span className="fw-light fs-6 d-block d-sm-inline">
                <span className="text-secondary"> + </span>
                Membership
              </span>
            )}
          </div>
          {(product.category === 'lab' || product.category === 'test') && !customerIsMember && (
            <div className="ms-0 ms-sm-auto">
              <button
                type="button"
                className="btn btn-link d-inline-block align-items-baseline"
                id="access-membership-modal-button"
                data-bs-toggle="modal"
                data-bs-target="#access-membership-modal"
              >
                Why do I need a membership?
              </button>
            </div>
          )}
        </div>

        {feeAndActions}
      </div>
    </div>
  )
}

PriceBlockWithoutQuantity.propTypes = {
  product: PropTypes.shape({
    price: PropTypes.string,
    is_applicable: PropTypes.bool,
    button_text: PropTypes.string,
    applicationUrl: PropTypes.string,
    is_purchasable: PropTypes.bool,
    category: PropTypes.string,
    service_category_id: PropTypes.number,
  }).isRequired,
  returnPolicy: PropTypes.shape({
    id: PropTypes.number,
    title: PropTypes.string,
  }),
  shippingPolicy: PropTypes.string,
  planTerms: PropTypes.string,
  customerIsMember: PropTypes.bool,
  accessMembershipPrice: PropTypes.string,
  annualAccessMembershipPrice: PropTypes.string,
  serviceFee: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  labCompanyName: PropTypes.string,
  defaultMembershipOption: PropTypes.string,
  membershipPlans: PropTypes.arrayOf(PropTypes.shape({
    sku: PropTypes.string,
    name: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  })),
  membershipPlansEnabled: PropTypes.bool,
  embedded: PropTypes.bool,
}
PriceBlockWithoutQuantity.defaultProps = {
  returnPolicy: null,
  shippingPolicy: null,
  planTerms: null,
  customerIsMember: null,
  accessMembershipPrice: null,
  annualAccessMembershipPrice: null,
  serviceFee: null,
  labCompanyName: null,
  defaultMembershipOption: null,
  membershipPlans: [],
  membershipPlansEnabled: false,
  embedded: false,
}
